<template>
  <div class="product-families-revisions-view">
    <revision-manager
      :fields="[
        'altName',
        'categories',
        'usps',
        'summaryTexts',
        'description',
        'legalTexts',
        'documents',
        'selectors'
      ]"
      :parent="productFamily"
      :revisions="productFamily.revisions"
      @add="addRevision"
      @delete="deleteRevision"
      @edit="editRevision"
      v-if="isSuccessfullyLoaded"
    >
      <span slot="title">
        <router-link
          :to="{ name: 'product-family', params: { id: productFamily.id }}"
        >{{productFamily.name}}</router-link>
      </span>

      <!-- Alt Name -->
      <template scope="altNameProps" slot="altName">
        <inheritable-value
          :inherited-value="productFamily.altName"
          :value="altNameProps.value"
        />
      </template>

      <!-- Categories -->
      <template scope="categoriesProps" slot="categories">
        <inheritable-value
          :inherited-value="productFamily.categories"
          :value="categoriesProps.value"
        >
          <template scope="{ value }" slot="value">
            <list :items="value" class="uk-margin-remove">
              <template scope="{ item }" slot="item">
                <router-link
                  :to="{ name: 'product-category', params: { id: item.id }}"
                >
                  <product-category-hierarchy :category="item" />
                  ({{ item.tenant.name }})
                </router-link>
              </template>
            </list>
          </template>
        </inheritable-value>
      </template>

      <!-- Description -->
      <template scope="description" slot="description">
        <inheritable-value
          :inherited-value="productFamily.description"
          :value="description.value"
        />
      </template>

      <!-- Documents -->
      <template scope="documentsProps" slot="documents">
        <inheritable-value
          :inherited-value="productFamily.documents"
          :value="documentsProps.value"
        >
          <template scope="{ value }" slot="value">
            <documents-cell :attachments="value" />
          </template>
        </inheritable-value>
      </template>

      <!-- Legal Texts -->
      <template scope="legalTextsProps" slot="legalTexts">
        <inheritable-value
          :inherited-value="productFamily.legalTexts"
          :value="legalTextsProps.value"
        >
          <template scope="{ value }" slot="value">
            <legal-texts-cell :legal-texts="value" />
          </template>
        </inheritable-value>
      </template>

      <!-- Summary Texts -->
      <template scope="summaryTextsProps" slot="summaryTexts">
        <inheritable-value
          :inherited-value="productFamily.summaryTexts"
          :value="summaryTextsProps.value"
        >
          <template scope="{ value }" slot="value">
            <list :items="value" class="uk-margin-remove" type="bullet">
              <text-placeholder slot="empty" />
            </list>
          </template>
        </inheritable-value>
      </template>

      <!-- USPs -->
      <template scope="uspsProps" slot="usps">
        <inheritable-value
          :inherited-value="productFamily.usps"
          :value="uspsProps.value"
        >
          <template scope="{ value }" slot="value">
            <list :items="value" class="uk-margin-remove" type="bullet">
              <text-placeholder slot="empty" />
            </list>
          </template>
        </inheritable-value>
      </template>
    </revision-manager>

    <div
      class="uk-text-center uk-margin-large uk-margin-large-top uk-margin-large-left uk-margin-large-right"
      v-else
    >
      <loader />
    </div>
  </div>
</template>

<script>
import deleteProductFamilyRevisionMutation
  from '@/api/mutations/delete-product-family-revision.graphql';
import Dimensions from '@/components/Dimensions';
import DocumentsCell from '@/components/DocumentsCell';
import EnergyEfficiencyClass from '@/components/EnergyEfficiencyClass';
import InheritableValue from '@/components/InheritableValue';
import LegalTextsCell from '@/components/LegalTextsCell';
import List from '@/components/List';
import Loader from '@/components/Loader';
import productFamilyQuery from '@/api/queries/product-family.graphql';
import ProductCategoryHierarchy from '@/components/ProductCategoryHierarchy';
import RevisionManager from '@/components/RevisionManager';
import Submittable from '@/mixins/Submittable';
import TextPlaceholder from '@/components/TextPlaceholder';
import Toast from '@/helpers/Toast';
import Thumbnail from '@/components/Thumbnail';
import View from '@/mixins/View';
import Weight from '@/components/Weight';

export default {
  name: 'product-families-revisions-view',
  mixins: [
    Submittable,
    View,
  ],
  components: {
    Dimensions,
    DocumentsCell,
    EnergyEfficiencyClass,
    InheritableValue,
    LegalTextsCell,
    List,
    Loader,
    ProductCategoryHierarchy,
    RevisionManager,
    TextPlaceholder,
    Thumbnail,
    Weight,
  },
  computed: {
    id() {
      return this.$route.params.id;
    },
    isSuccessfullyLoaded() {
      return this.isLoaded && this.productFamily;
    },
  },
  apollo: {
    productFamily: {
      query: productFamilyQuery,
      variables() {
        return { id: this.id };
      },
      fetchPolicy: 'network-only',
      loadingKey: 'loading',
      update({ productFamily }) {
        return productFamily;
      },
      skip() {
        return !this.isAuthenticated;
      },
    },
  },
  methods: {
    addRevision() {
      this.$router.push({
        name: 'new-product-family-revision',
        params: { productFamilyId: this.id },
      });
    },
    editRevision(revision) {
      this.$router.push({
        name: 'edit-product-family-revision',
        params: { id: revision.id },
      });
    },
    async deleteRevision(revision) {
      await this.$apollo.mutate({
        mutation: deleteProductFamilyRevisionMutation,
        variables: { id: revision.id },
        refetchQueries: [
          {
            query: productFamilyQuery,
            variables: { id: this.id },
          },
        ],
      });
      Toast.success(this.$t('toasts.deleted'));
    },
  },
  i18n: {
    messages: {
      de: {
        confirmations: {
          delete: 'Möchtest du die ausgewählte Revision wirklich löschen?',
        },
        toasts: {
          deleted: 'Die Revision wurde gelöscht',
        },
      },
    },
  },
};
</script>



// WEBPACK FOOTER //
// RevisionsView.vue?75c3ec78